import React, { useEffect, useState, useCallback } from "react";
import { Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import LetterStatusCard from "@/components/LetterStatusCard";
import StatusBadge from "@/components/StatusBadge";
import PageHeader, { ActionRow, EmptyState, fmtDate, schoolYear } from "@/components/PageHeader";
import { shortNo } from "@/lib/caseNo";

const schoolYearOf = (dateStr) => {
  if (!dateStr) return null;
  const [y, m] = dateStr.slice(0, 10).split("-").map(Number);
  if (!y || !m) return null;
  return m >= 6 ? y : y - 1;
};

function SubmissionDialog({ open, setOpen, options, editing, onDone }) {
  const [form, setForm] = useState({ event_date: "", achievement_option_id: "", event_description: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editing) setForm({ event_date: (editing.event_date || "").slice(0, 10), achievement_option_id: editing.achievement_option_id, event_description: editing.event_description });
    else setForm({ event_date: "", achievement_option_id: "", event_description: "" });
  }, [editing, open]);

  const submit = async () => {
    if (!form.event_date || !form.achievement_option_id || !form.event_description) {
      toast.error("All fields are required"); return;
    }
    setLoading(true);
    try {
      if (editing) await api.put(`/member/lettering/${editing.id}`, form);
      else await api.post("/member/lettering", form);
      toast.success(editing ? "Submission updated" : "Submission created");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="lettering-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit submission" : "New lettering submission"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Event date</Label>
            <Input type="date" value={form.event_date} onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))} data-testid="lettering-eventdate-input" />
          </div>
          <div className="space-y-2">
            <Label>Achievement</Label>
            <Select value={form.achievement_option_id} onValueChange={(v) => setForm((f) => ({ ...f, achievement_option_id: v }))}>
              <SelectTrigger data-testid="lettering-achievement-select"><SelectValue placeholder="Select achievement" /></SelectTrigger>
              <SelectContent>
                {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label} · {o.points} pts</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Event description</Label>
            <Textarea rows={3} value={form.event_description} onChange={(e) => setForm((f) => ({ ...f, event_description: e.target.value }))} data-testid="lettering-description-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="lettering-submit-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">{editing ? "Save" : "Submit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const editLabel = (s) => (s.status === "returned_for_correction" ? "Fix & resubmit" : "Edit");

function CoachNote({ s }) {
  return s.returned_comment ? <p className="mt-1 text-xs text-violet-700" data-testid={`lettering-coach-note-${s.case_number}`}>Coach: {s.returned_comment}</p> : null;
}

export default function MemberLettering() {
  const [data, setData] = useState({ items: [], approved_lettering_points: 0 });
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/member/lettering"), api.get("/member/achievement-options")]);
    setData(a.data); setOptions(b.data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = (s) => !s.locked && ["pending", "returned_for_correction"].includes(s.status);
  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setOpen(true); };

  const [downloading, setDownloading] = useState(false);
  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/member/lettering/certificate", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "lettering-certificate.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Certificate downloaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Unable to download certificate");
    } finally { setDownloading(false); }
  };

  const [syFilter, setSyFilter] = useState("all");
  const years = [...new Set(data.items.map((s) => schoolYearOf(s.event_date)).filter((y) => y !== null))].sort((a, b) => b - a);
  const visibleItems = syFilter === "all" ? data.items : data.items.filter((s) => schoolYearOf(s.event_date) === Number(syFilter));

  return (
    <div>
      <PageHeader title="Letter Points" subtitle="Submit lettering points and track your lettering awards" />

      <div className="mb-6"><LetterStatusCard status={data.letter_status} /></div>

      <ActionRow testId="new-lettering-row">
        <Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-lettering-btn"><Plus className="mr-2 h-4 w-4" /> New submission</Button>
      </ActionRow>

      {years.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">School year</Label>
          <Select value={syFilter} onValueChange={setSyFilter}>
            <SelectTrigger className="w-48" data-testid="lettering-schoolyear-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="sy-all">All school years</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`sy-${y}`}>{schoolYear(y)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {visibleItems.length === 0 ? (
        <EmptyState title="No submissions yet" description="Create your first lettering submission." testId="lettering-empty" />
      ) : (
        <>
          <Card className="hidden md:block border-slate-200 shadow-sm overflow-hidden">
            <Table data-testid="lettering-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Case #", "Event Date", "Achievement", "Points", "Description", "Submitted", "Status", ""].map((h) => (
                    <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleItems.map((s) => (
                  <TableRow key={s.id} data-testid={`lettering-row-${s.case_number}`}>
                    <TableCell className="whitespace-nowrap font-mono text-xs font-semibold">{shortNo(s.case_number)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm tabular-nums">{fmtDate(s.event_date)}</TableCell>
                    <TableCell className="max-w-xs text-sm font-medium text-slate-800">{s.achievement_label_snapshot}</TableCell>
                    <TableCell className={`font-bold tabular-nums ${s.status === "approved" ? "text-emerald-700" : "text-violet-700"}`}>{s.points_requested_snapshot}</TableCell>
                    <TableCell className="max-w-xs text-sm text-slate-600">{s.event_description}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm tabular-nums">{fmtDate(s.date_submitted)}</TableCell>
                    <TableCell className="max-w-[14rem]"><StatusBadge status={s.status} /><CoachNote s={s} /></TableCell>
                    <TableCell>{canEdit(s) && <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(s)} data-testid={`edit-lettering-${s.case_number}`}>{editLabel(s)}</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="space-y-3 md:hidden" data-testid="lettering-cards">
            {visibleItems.map((s) => (
              <Card key={s.id} className="border-slate-200 p-4" data-testid={`lettering-card-${s.case_number}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{s.achievement_label_snapshot}</p>
                    <p className="font-mono text-[11px] text-slate-400">{shortNo(s.case_number)}</p>
                  </div>
                  <span className={`font-bold tabular-nums ${s.status === "approved" ? "text-emerald-700" : "text-violet-700"}`}>{s.points_requested_snapshot} pts</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{fmtDate(s.event_date)} · {s.event_description}</p>
                <div className="mt-2"><StatusBadge status={s.status} /></div>
                <CoachNote s={s} />
                {canEdit(s) && <Button size="sm" variant="outline" className="mt-3 rounded-full" onClick={() => openEdit(s)}>{editLabel(s)}</Button>}
              </Card>
            ))}
          </div>
        </>
      )}

      <SubmissionDialog open={open} setOpen={setOpen} options={options} editing={editing} onDone={load} />
    </div>
  );
}
